"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Phone, Mail, Clock, ChevronRight, ShieldCheck, Briefcase, Building2, Scale } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu, MenuItem } from "@/integrations/wordpress/WordPressMenuProvider";
interface NavBranchProps {
  items: MenuItem[];
  level?: number;
}
function RecursiveFooterMenuList({
  items,
  level = 0
}: NavBranchProps) {
  if (!items || items.length === 0) return null;
  return <ul className={level === 0 ? "space-y-2.5" : "mt-2 space-y-2 pl-3 border-l border-border/60"}>
      {items.map((item, i) => {
      const hasChildren = Boolean(item.children && item.children.length > 0);
      return <li key={item.id || `${item.label}-${i}`} data-index={i} className="text-sm">
            <Link to={item.href || "#"} className="group inline-flex items-center text-muted-foreground transition-colors hover:text-foreground">
              <ChevronRight className="mr-1.5 size-3.5 text-ring transition-transform group-hover:translate-x-0.5" />
              <span>{item.label}</span>
            </Link>
            {hasChildren && item.children && <RecursiveFooterMenuList items={item.children} level={level + 1} />}
          </li>;
    })}
    </ul>;
}
function FooterMenuConsumer() {
  const {
    menuItems,
    loading,
    error
  } = useMenu();
  if (loading) {
    return <div className="space-y-2.5">
        <div className="h-4 w-28 animate-pulse rounded-sm bg-muted" />
        <div className="h-4 w-36 animate-pulse rounded-sm bg-muted" />
        <div className="h-4 w-24 animate-pulse rounded-sm bg-muted" />
        <div className="h-4 w-32 animate-pulse rounded-sm bg-muted" />
      </div>;
  }
  if (error || !menuItems || menuItems.length === 0) {
    const fallbackLinks = [{
      id: "fb-1",
      label: "Inicio",
      href: "/HomePage"
    }, {
      id: "fb-2",
      label: "Gobierno",
      href: "/GovernmentPage"
    }, {
      id: "fb-3",
      label: "Empresas",
      href: "/CompaniesPage"
    }, {
      id: "fb-4",
      label: "Jurídico",
      href: "/LegalPage"
    }, {
      id: "fb-5",
      label: "Licitaciones",
      href: "/TendersPage"
    }, {
      id: "fb-6",
      label: "Consultoría en Línea",
      href: "/OnlineConsultingPage"
    }, {
      id: "fb-7",
      label: "Capacitación",
      href: "/TrainingPage"
    }, {
      id: "fb-8",
      label: "Contacto",
      href: "/ContactPage"
    }];
    return <RecursiveFooterMenuList items={fallbackLinks} />;
  }
  return <RecursiveFooterMenuList items={menuItems} />;
}
const institutionalPillars = [{
  id: "pl-1",
  label: "Gobierno",
  icon: Building2
}, {
  id: "pl-2",
  label: "Empresa",
  icon: Briefcase
}, {
  id: "pl-3",
  label: "Legal",
  icon: Scale
}];
export default function Footer() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="footer" data-nav="light" className="relative overflow-hidden border-t border-border bg-[hsl(40_30%_97%)] text-foreground pt-16 pb-12 lg:pt-20 lg:pb-14">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-12 lg:gap-8">
          {/* Column 1: Monogram and Institutional Identity */}
          <motion.div variants={itemVariants} className="space-y-6 lg:col-span-5">
            <div>
              <Link to="/">
                <WvcLogo className="h-12 w-auto" />
              </Link>
            </div>

            <div className="space-y-3">
              <div className="h-0.5 w-12 bg-ring" />
              <p className="font-serif text-sm font-semibold tracking-wider text-foreground">
                Estrategia · Cumplimiento · Resultados
              </p>
              <p className="text-sm leading-relaxed text-muted-foreground">
                Firma mexicana de consultoría multidisciplinaria organizada en tres divisiones estratégicas:
                Gobierno, Empresa y Legal. Asesoría técnica integral y defensa jurídica de alto nivel.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-1">
              {institutionalPillars.map((pillar, i) => {
              const IconComponent = pillar.icon;
              return <div key={pillar.id} data-index={i} className="inline-flex items-center gap-1.5 rounded-sm border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground shadow-2xs">
                    <IconComponent className="size-3.5 text-ring" />
                    <span>{pillar.label}</span>
                  </div>;
            })}
            </div>
          </motion.div>

          {/* Column 2: Institutional Navigation */}
          <motion.div variants={itemVariants} className="space-y-4 lg:col-span-3">
            <div className="border-b border-border/80 pb-2.5">
              <h4 className="font-serif text-xs font-bold tracking-widest text-foreground uppercase">
                Enlaces Institucionales
              </h4>
            </div>
            <MenuProvider menu_id="3" className="w-full">
              <FooterMenuConsumer />
            </MenuProvider>
          </motion.div>

          {/* Column 3: Physical Headquarters & Official Contacts */}
          <motion.div variants={itemVariants} className="space-y-4 lg:col-span-4">
            <div className="border-b border-border/80 pb-2.5">
              <h4 className="font-serif text-xs font-bold tracking-widest text-foreground uppercase">
                Sede y Contacto
              </h4>
            </div>
            <div className="space-y-4 text-sm text-muted-foreground">
              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 size-4 shrink-0 text-ring" />
                <address className="not-italic leading-snug text-foreground">
                  Av. Rosa Morada 1360, Fracc. Praderas, Los Mochis, Ahome, Sinaloa, México
                </address>
              </div>

              <div className="flex items-center gap-3">
                <Phone className="size-4 shrink-0 text-ring" />
                <div>
                  <span className="block text-xs uppercase tracking-wider text-muted-foreground">Teléfono directo</span>
                  <a href="tel:6672505982" className="text-foreground transition-colors hover:text-ring">
                    667 250 5982
                  </a>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Mail className="size-4 shrink-0 text-ring" />
                <div>
                  <span className="block text-xs uppercase tracking-wider text-muted-foreground">Atención oficial</span>
                  <a href="mailto:javilez@avsconsultores.org" className="text-foreground transition-colors hover:text-ring">
                    javilez@avsconsultores.org
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Clock className="mt-0.5 size-4 shrink-0 text-ring" />
                <div>
                  <span className="block text-xs uppercase tracking-wider text-muted-foreground">Horario corporativo</span>
                  <span className="text-foreground">Lunes a Viernes: 9:00 - 18:00</span>
                </div>
              </div>

              <div className="pt-2">
                <Button asChild variant="outline" size="sm" className="w-full justify-center border-ring/60 bg-card font-serif text-xs tracking-wider text-foreground hover:bg-ring hover:text-primary-foreground">
                  <Link to="/ContactPage">
                    Solicitar Asesoría
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Horizontal Hairline Divider */}
        <div className="my-10">
          <Separator className="bg-border" />
        </div>

        {/* Bottom Bar: Disclaimers and Copyright */}
        <div className="flex flex-col items-center justify-between gap-4 text-xs text-muted-foreground md:flex-row">
          <div className="flex items-center gap-2 text-center md:text-left">
            <ShieldCheck className="size-4 shrink-0 text-ring" />
            <span className="text-muted-foreground">
              © {new Date().getFullYear()} ASV CONSULTORES. Todos los derechos reservados. Firma con respaldo profesional.
            </span>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-6">
            <Link to="/AboutUsPage" className="text-muted-foreground transition-colors hover:text-foreground">
              Aviso de Privacidad
            </Link>
            <span className="text-border select-none">|</span>
            <Link to="/GovernmentPage" className="text-muted-foreground transition-colors hover:text-foreground">
              Normativa CONAC
            </Link>
            <span className="text-border select-none">|</span>
            <Link to="/ContactPage" className="text-muted-foreground transition-colors hover:text-foreground">
              Oficinas Generales
            </Link>
          </div>
        </div>
      </div>
    </section>;
}